<template>
  <div class="offers-manager mx-lg-3 mx-1">
    <!-- Create/Edit Offer Form -->
    <div class="offer-panel" :class="{ 'offer-panel--editing': isEditing }">
      <div class="offer-panel-header">
        <div>
          <h5 class="panel-title mb-1">{{ isEditing ? 'Editar oferta' : 'Nueva oferta' }}</h5>
          <p class="text-muted small mb-0 header-sub">Asistente en {{ WIZARD_STEP_COUNT }} pasos: te guiamos en cada dato y para qué sirve.</p>
          <div v-if="isEditing" class="edit-mode-alert mt-2" role="status">
            <i class="fas fa-pen-fancy me-2"></i>
            <span>Modo edición: el formulario está cargado con <strong>{{ offerForm.name || 'esta oferta' }}</strong>. Revisá los pasos y guardá para aplicar los cambios.</span>
          </div>
        </div>
        <button v-if="isEditing" class="btn btn-cancel-edit btn-sm" @click="cancelEdit">
          <i class="fas fa-times me-2"></i>Cancelar
        </button>
      </div>
      <div class="offer-panel-body">
        <form @submit.prevent>
          <!-- Indicador de pasos -->
          <div class="wizard-stepper mb-4">
            <div class="wizard-stepper-track">
              <div class="wizard-stepper-line" :style="{ width: wizardProgress }" aria-hidden="true"></div>
            </div>
            <ol class="wizard-stepper-list" role="list">
              <li
                v-for="(st, idx) in wizardStepMeta"
                :key="st.id"
                class="wizard-stepper-item"
                :class="{
                  'is-active': wizardStep === st.id,
                  'is-done': wizardStep > st.id
                }"
              >
                <button
                  type="button"
                  class="wizard-stepper-btn"
                  :disabled="!canJumpToStep(st.id)"
                  :title="st.hint"
                  @click="goToStep(st.id)"
                >
                  <span class="wizard-stepper-num"><i v-if="wizardStep > st.id" class="fas fa-check"></i><template v-else>{{ idx + 1 }}</template></span>
                  <span class="wizard-stepper-label d-none d-md-inline">{{ st.shortTitle }}</span>
                </button>
              </li>
            </ol>
            <p class="wizard-step-hint text-center small text-muted mt-2 mb-0">
              Paso {{ wizardStep }} de {{ WIZARD_STEP_COUNT }} · {{ currentStepMeta.title }}
            </p>
          </div>

          <!-- Paso 1: Nombre y descuento base -->
          <div v-show="wizardStep === 1" class="wizard-step">
            <div class="step-intro">
              <h6 class="step-title"><i class="fas fa-tag me-2 text-brand"></i>{{ wizardStepMeta[0].title }}</h6>
              <p class="step-lead">{{ wizardStepMeta[0].lead }}</p>
            </div>
            <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Nombre de la oferta</label>
              <input
                v-model="offerForm.name"
                type="text"
                class="form-control"
                placeholder="Ej: Semana de vidrios templados"
                autocomplete="off"
              >
              <small class="text-muted">Así la vas a identificar en el listado y en reportes. Usá un nombre claro para tu equipo.</small>
            </div>
            <div class="col-md-6">
              <label class="form-label">Descuento base (%)</label>
              <input
                v-model.number="offerForm.discount"
                type="number"
                class="form-control"
                min="0"
                max="100"
                step="0.01"
              >
              <small class="text-muted">Porcentaje de descuento sobre el precio. En el paso 3 podés exigir compra mínima, poner tope al descuento o limitar la oferta a mayoristas.</small>
            </div>
            </div>
          </div>

          <!-- Paso 2: Vigencia -->
          <div v-show="wizardStep === 2" class="wizard-step">
            <div class="step-intro">
              <h6 class="step-title"><i class="fas fa-calendar-alt me-2 text-brand"></i>{{ wizardStepMeta[1].title }}</h6>
              <p class="step-lead">{{ wizardStepMeta[1].lead }}</p>
            </div>
            <div class="row g-3">
            <div class="col-12">
              <label class="form-label d-block">Vigencia de la oferta</label>
              <div class="row g-3 align-items-stretch vigencia-side-by-side">
                <div class="col-lg-6">
                  <div class="vigencia-card h-100">
                    <div class="vigencia-card-title">Inicio</div>
                    <div class="row g-2 align-items-end">
                      <div class="col-7">
                        <label class="form-label sub-label">Fecha</label>
                        <div class="input-group">
                          <span class="input-group-text" aria-hidden="true"><i class="fas fa-calendar-day"></i></span>
                          <input
                            v-model="offerForm.startDate"
                            type="date"
                            class="form-control"
                            required
                          >
                        </div>
                      </div>
                      <div class="col-5">
                        <label class="form-label sub-label">Hora</label>
                        <div class="input-group">
                          <span class="input-group-text" aria-hidden="true"><i class="fas fa-clock"></i></span>
                          <input
                            v-model="offerForm.startTime"
                            type="time"
                            class="form-control"
                            required
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-6">
                  <div class="vigencia-card h-100">
                    <div class="vigencia-card-title">Fin</div>
                    <div class="row g-2 align-items-end">
                      <div class="col-7">
                        <label class="form-label sub-label">Fecha</label>
                        <div class="input-group">
                          <span class="input-group-text" aria-hidden="true"><i class="fas fa-calendar-day"></i></span>
                          <input
                            v-model="offerForm.endDate"
                            type="date"
                            class="form-control"
                            required
                          >
                        </div>
                      </div>
                      <div class="col-5">
                        <label class="form-label sub-label">Hora</label>
                        <div class="input-group">
                          <span class="input-group-text" aria-hidden="true"><i class="fas fa-clock"></i></span>
                          <input
                            v-model="offerForm.endTime"
                            type="time"
                            class="form-control"
                            required
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>

          <!-- Paso 3: Límites y público -->
          <div v-show="wizardStep === 3" class="wizard-step">
            <div class="step-intro">
              <h6 class="step-title"><i class="fas fa-balance-scale me-2 text-brand"></i>{{ wizardStepMeta[2].title }}</h6>
              <p class="step-lead">{{ wizardStepMeta[2].lead }}</p>
            </div>

            <div class="offer-rules-step">
              <p class="rules-step-section-title">Montos (todo opcional)</p>
              <div class="row g-3 align-items-stretch">
                <div class="col-lg-6">
                  <div class="rule-field-card h-100">
                    <div class="rule-field-card__head">
                      <span class="rule-field-card__badge" aria-hidden="true"><i class="fas fa-shopping-basket"></i></span>
                      <div>
                        <label class="form-label rule-field-card__label" for="offerMinPurchase">Compra mínima</label>
                        <p class="rule-field-card__sub">Por cada producto en el carrito</p>
                      </div>
                    </div>
                    <div class="input-group input-group-offer">
                      <span class="input-group-text">$</span>
                      <input
                        id="offerMinPurchase"
                        v-model="offerForm.min_purchase"
                        type="number"
                        class="form-control"
                        min="0"
                        step="0.01"
                        placeholder="Sin mínimo"
                      >
                    </div>
                    <div class="rule-field-card__explain">
                      <p class="mb-0">
                        Se mira el <strong>subtotal de ese producto</strong> (lo que paga el cliente: precio unitario × cantidad).
                        El descuento de la oferta <strong>solo aplica</strong> si ese subtotal es mayor o igual al mínimo.
                      </p>
                    </div>
                  </div>
                </div>
                <div class="col-lg-6">
                  <div class="rule-field-card h-100">
                    <div class="rule-field-card__head">
                      <span class="rule-field-card__badge rule-field-card__badge--cap" aria-hidden="true"><i class="fas fa-coins"></i></span>
                      <div>
                        <label class="form-label rule-field-card__label" for="offerMaxDisc">Tope al descuento</label>
                        <p class="rule-field-card__sub">Cada producto, por separado</p>
                      </div>
                    </div>
                    <div class="input-group input-group-offer">
                      <span class="input-group-text">$</span>
                      <input
                        id="offerMaxDisc"
                        v-model="offerForm.max_discount_amount"
                        type="number"
                        class="form-control"
                        min="0"
                        step="0.01"
                        placeholder="Sin tope"
                      >
                    </div>
                    <div class="rule-field-card__explain">
                      <p class="mb-0">
                        Tope en pesos al <strong>ahorro de la oferta</strong> en cada ítem (no al precio de venta).
                        Sirve para no regalar de más. Vacío = sin tope.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <p class="rules-step-section-title rules-step-section-title--spaced">Quién puede usar la oferta</p>
              <div class="rule-audience-card">
                <div class="form-check form-switch mb-0">
                  <input
                    id="distributorsOnlySwitch"
                    v-model="offerForm.distributors_only"
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                  >
                  <label class="form-check-label" for="distributorsOnlySwitch">Solo clientes con precio mayorista</label>
                </div>
                <p class="rule-audience-hint">
                  Encendelo si la promoción es <strong>exclusiva</strong> de quienes compran con lista mayorista (no la ve el público en general).
                </p>
              </div>
            </div>
          </div>

          <!-- Paso 4: Productos -->
          <div v-show="wizardStep === 4" class="wizard-step">
            <div class="step-intro">
              <h6 class="step-title"><i class="fas fa-box-open me-2 text-brand"></i>{{ wizardStepMeta[3].title }}</h6>
              <p class="step-lead">{{ wizardStepMeta[3].lead }}</p>
            </div>
            <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Productos incluidos en esta oferta</label>
              <p class="small text-muted mb-2">Solo estos ítems tendrán el descuento de la oferta mientras la promoción esté vigente. Podés sumar de a uno o traer un lote por categoría.</p>
              <!-- Tabs para seleccionar por productos individuales o por categorías -->
              <div class="mb-3">
                <div class="btn-group" role="group">
                  <button 
                    type="button" 
                    class="btn tab-btn" 
                    :class="!showCategorySelector ? 'tab-btn-active' : ''"
                    @click="showCategorySelector = false"
                  >
                    <i class="fas fa-box me-2"></i>Por Productos
                  </button>
                  <button 
                    type="button" 
                    class="btn tab-btn" 
                    :class="showCategorySelector ? 'tab-btn-active' : ''"
                    @click="showCategorySelector = true"
                  >
                    <i class="fas fa-tags me-2"></i>Por Categorías
                  </button>
                </div>
              </div>

              <!-- Selección por productos individuales -->
              <div v-if="!showCategorySelector">
                <div class="search-products mb-3">
                  <div class="input-group">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="searchQuery"
                      placeholder="Buscar productos..."
                      @input="filterProducts"
                    >
                    <span class="input-group-text">
                      <i class="fas fa-search"></i>
                    </span>
                  </div>
                </div>
                
                <!-- Lista de productos filtrados -->
                <div class="products-list mb-3" v-if="searchQuery">
                  <div class="list-group">
                    <!-- Loading -->
                    <div v-if="isSearching" class="list-group-item text-center">
                      <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
                        <span class="visually-hidden">Buscando...</span>
                      </div>
                      <span class="text-muted">Buscando productos...</span>
                    </div>
                    <!-- Sin resultados -->
                    <div v-else-if="filteredProducts.length === 0" class="list-group-item text-center text-muted">
                      <i class="fas fa-search me-2"></i>
                      No se encontraron productos para "{{ searchQuery }}"
                    </div>
                    <!-- Resultados -->
                    <button
                      v-else
                      type="button"
                      v-for="product in filteredProducts"
                      :key="product.id"
                      class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      @click="toggleProductSelection(product)"
                      :class="{ active: offerForm.products.includes(product.id) }"
                    >
                      <div>
                        <strong>{{ product.name }}</strong>
                        <small class="d-block text-muted">{{ product.brand }}</small>
                      </div>
                      <div class="d-flex align-items-center">
                        <span class="price me-3">${{ product.price }}</span>
                        <i :class="offerForm.products.includes(product.id) ? 'fas fa-check-circle text-brand' : 'fas fa-plus-circle'"></i>
                      </div>
                    </button>
                  </div>
                </div>
              </div>              <!-- Selección por categorías -->
              <div v-else>
                <div class="categories-selector mb-3">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="text-white mb-0">Seleccionar Categorías</h6>
                    <button 
                      v-if="selectedCategories.length > 0" 
                      type="button" 
                      class="btn btn-clear-selection btn-sm"
                      @click="clearSelectedCategories"
                    >
                      <i class="fas fa-times me-1"></i>Limpiar selección
                    </button>
                  </div>                  <div class="categories-list">                    <div v-if="flatCategories.length === 0" class="text-center text-muted py-4">
                      <i class="fas fa-tags fa-2x mb-2"></i>
                      <p>No hay categorías disponibles</p>
                      <small>Las categorías se cargarán automáticamente del servidor</small>
                    </div>
                    <div v-else class="list-group">
                      <button
                        type="button"
                        v-for="category in flatCategories"
                        :key="category.id"
                        class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                        @click="toggleCategorySelection(category)"
                        :class="{ 
                          active: selectedCategories.includes(category.id),
                          'subcategory': category.level > 0
                        }"
                      >                        <div>
                          <strong>{{ category.displayName }}</strong>
                          <div class="d-flex align-items-center mt-1">
                            <small class="text-muted me-2">
                              <i class="fas fa-box me-1"></i>{{ getCategoryProductCount(category.id) }} productos
                            </small>
                            <small v-if="category.description" class="text-muted">
                              • {{ category.description }}
                            </small>
                          </div>
                        </div>
                        <i :class="selectedCategories.includes(category.id) ? 'fas fa-check-circle text-brand' : 'fas fa-plus-circle'"></i>
                      </button>
                    </div>
                  </div>
                  <div v-if="selectedCategories.length > 0" class="mt-3">
                    <div class="category-selection-summary">
                      <span class="category-selection-summary__icon" aria-hidden="true"><i class="fas fa-layer-group" /></span>
                      <p class="mb-0">
                        <strong>Selección de categorías:</strong> {{ selectedCategories.length }} categoría(s),
                        <span class="text-nowrap">≈ {{ getTotalProductsFromSelectedCategories() }} productos</span> únicos.
                      </p>
                    </div>
                    <button 
                      type="button" 
                      class="btn btn-add-from-categories" 
                      @click="addProductsFromCategories"
                      :disabled="isAddingCategoryProducts"
                    >
                      <i v-if="isAddingCategoryProducts" class="fas fa-spinner fa-spin me-2"></i>
                      <i v-else class="fas fa-plus me-2"></i>
                      {{ isAddingCategoryProducts ? 'Agregando productos...' : `Agregar ${getTotalProductsFromSelectedCategories()} productos de las categorías seleccionadas` }}
                    </button>
                  </div>
                </div>
              </div><!-- Lista de productos seleccionados -->
              <div class="selected-products">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="mb-0 text-white">Productos seleccionados ({{ offerForm.products.length }})</h6>
                  <button 
                    v-if="offerForm.products.length > 0" 
                    type="button" 
                    class="btn btn-clear-selection btn-sm"
                    @click="clearAllProducts"
                  >
                    <i class="fas fa-trash me-1"></i>Limpiar todos
                  </button>
                </div>
                <div v-if="offerForm.products.length === 0" class="text-center text-muted py-3">
                  <i class="fas fa-box-open fa-2x mb-2"></i>
                  <p>No hay productos seleccionados</p>
                  <small>Usa las pestañas de arriba para agregar productos individualmente o por categorías</small>
                </div>
                <div v-else class="list-group">
                  <div
                    v-for="productId in offerForm.products"
                    :key="productId"
                    class="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <strong>{{ getProductName(productId) }}</strong>
                      <div class="small text-muted">
                        <span v-if="getProductBrand(productId)">{{ getProductBrand(productId) }} • </span>
                        <span v-if="getProduct(productId)?.category">{{ getCategoryName(getProduct(productId).category) }}</span>
                      </div>
                    </div>
                    <div class="d-flex align-items-center">
                      <span v-if="getProduct(productId)?.price" class="price me-3">${{ getProduct(productId).price }}</span>
                      <button 
                        type="button" 
                        class="btn btn-remove-item btn-sm"
                        @click="removeProduct(productId)"
                      >
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>

          <!-- Paso 5: Revisión -->
          <div v-show="wizardStep === 5" class="wizard-step wizard-step-review">
            <div class="step-intro">
              <h6 class="step-title"><i class="fas fa-clipboard-check me-2 text-brand"></i>{{ wizardStepMeta[4].title }}</h6>
              <p class="step-lead">{{ wizardStepMeta[4].lead }}</p>
            </div>
            <div class="review-card">
              <dl class="review-dl">
                <div class="review-row">
                  <dt>Nombre</dt>
                  <dd>{{ offerForm.name || '—' }}</dd>
                </div>
                <div class="review-row">
                  <dt>Descuento base</dt>
                  <dd>{{ offerForm.discount }}%</dd>
                </div>
                <div class="review-row">
                  <dt>Vigencia</dt>
                  <dd>
                    {{ offerForm.startDate || '—' }} {{ offerForm.startTime }} →
                    {{ offerForm.endDate || '—' }} {{ offerForm.endTime }}
                  </dd>
                </div>
                <div class="review-row">
                  <dt>Compra mín. / tope desc.</dt>
                  <dd>
                    <span v-if="numOrNull(offerForm.min_purchase) != null">Mín. subtotal ${{ offerForm.min_purchase }}</span>
                    <span v-else>Sin mínimo</span>
                    <span class="text-muted mx-1">·</span>
                    <span v-if="numOrNull(offerForm.max_discount_amount) != null">Tope ahorro ${{ offerForm.max_discount_amount }}</span>
                    <span v-else>Sin tope</span>
                  </dd>
                </div>
                <div class="review-row">
                  <dt>Alcance</dt>
                  <dd>
                    <span v-if="offerForm.distributors_only" class="badge bg-secondary">Solo mayoristas</span>
                    <span v-else>Todos los clientes elegibles (según reglas)</span>
                  </dd>
                </div>
                <div class="review-row">
                  <dt>Productos</dt>
                  <dd>
                    <strong>{{ offerForm.products.length }}</strong> producto(s) en oferta
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <!-- Navegación del asistente -->
          <div class="wizard-nav">
            <button
              v-if="wizardStep > 1"
              type="button"
              class="btn btn-wizard-back"
              @click="goWizardBack"
            >
              <i class="fas fa-arrow-left me-2"></i>Atrás
            </button>
            <div class="flex-grow-1" />
            <button
              v-if="wizardStep < WIZARD_STEP_COUNT"
              type="button"
              class="btn btn-wizard-next"
              @click="goWizardNext"
            >
              Siguiente<i class="fas fa-arrow-right ms-2"></i>
            </button>
            <button
              v-if="wizardStep === WIZARD_STEP_COUNT"
              type="button"
              class="btn btn-submit"
              :disabled="isSubmitting"
              @click="handleSubmit"
            >
              <i class="fas fa-save me-2"></i>
              {{ isSubmitting ? 'Guardando...' : (isEditing ? 'Guardar cambios' : 'Crear oferta') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Active Offers List -->
    <div class="offer-panel mt-3">
      <div class="offer-panel-header">
        <h5 class="panel-title">Ofertas activas</h5>
      </div>
      <div class="offer-panel-body">
        <div v-if="isLoadingOffers" class="offers-loading-state text-center py-5 text-muted">
          <div class="spinner-border text-brand offers-loading-spin" role="status" aria-label="Cargando ofertas" />
          <p class="mt-3 mb-0">Cargando ofertas activas…</p>
        </div>
        <div v-else-if="offers.length === 0" class="text-center text-muted py-4">
          <i class="fas fa-percentage fa-2x mb-2"></i>
          <p class="mb-0">No hay ofertas activas</p>
        </div>
        <div v-else class="table-responsive">
          <table class="table offers-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descuento</th>
                <th>Condiciones</th>
                <th>Fecha Inicio</th>
                <th>Fecha Fin</th>
                <th>Estado</th>
                <th>Productos</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="offer in offers" :key="offer.id">
                <td>{{ offer.name }}</td>
                <td>{{ offer.discount }}%</td>
                <td>
                  <div class="small text-muted offer-cond-cell">
                    <span v-if="offer.distributors_only" class="badge bg-secondary me-1 mb-1">Solo dist.</span>
                    <span v-if="hasOfferMin(offer)" class="d-block">Mín. subtotal ${{ offer.min_purchase }}</span>
                    <span v-if="hasOfferCap(offer)" class="d-block">Tope ${{ offer.max_discount_amount }}</span>
                    <span
                      v-if="!offer.distributors_only && !hasOfferMin(offer) && !hasOfferCap(offer)"
                    >—</span>
                  </div>
                </td>
                <td>{{ formatDate(offer.start_date) }}</td>
                <td>{{ formatDate(offer.end_date) }}</td>
                <td>
                  <span :class="['badge', getStatusBadgeClass(offer)]">
                    {{ getStatusText(offer) }}
                  </span>
                </td>
                <td>{{ offer.products?.length || 0 }}</td>
                <td>
                  <div v-if="deletingOfferId === offer.id" class="d-inline-flex align-items-center gap-2 text-muted small">
                    <span class="spinner-border spinner-border-sm text-danger" role="status" />
                    <span>Procesando…</span>
                  </div>
                  <div v-else class="btn-group">
                    <button
                      class="btn btn-sm btn-action btn-action-edit"
                      :disabled="isSubmitting"
                      @click="editOffer(offer)"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button
                      class="btn btn-sm btn-action btn-action-delete"
                      :disabled="isSubmitting || deletingOfferId != null"
                      type="button"
                      :aria-label="'Eliminar oferta ' + (offer.name || '')"
                      @click="openDeleteOfferModal(offer)"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="showDeleteOfferModal && offerPendingDelete"
      class="offer-delete-overlay"
      @click.self="closeDeleteOfferModal"
    >
      <div
        class="offer-delete-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="offer-delete-title"
      >
        <div class="offer-delete-card__head">
          <div class="offer-delete-card__icon" aria-hidden="true">
            <i class="fas fa-exclamation-triangle" />
          </div>
          <h3 id="offer-delete-title" class="offer-delete-card__title">Eliminar oferta</h3>
          <button
            type="button"
            class="offer-delete-card__close"
            aria-label="Cerrar"
            :disabled="deletingOfferId != null"
            @click="closeDeleteOfferModal"
          >
            <i class="fas fa-times" />
          </button>
        </div>
        <p class="offer-delete-card__lead text-muted small mb-2">
          Esta acción quita la promoción del catálogo. Los productos en el listado de la tienda
          <strong>no se borran</strong>.
        </p>
        <div class="offer-delete-card__summary">
          <div class="offer-delete-name">{{ offerPendingDelete.name }}</div>
          <ul class="offer-delete-meta list-unstyled small mb-0 text-muted">
            <li v-if="offerPendingDelete.discount != null">
              <i class="fas fa-percentage me-1 text-brand" aria-hidden="true" />
              {{ offerPendingDelete.discount }}% de descuento
            </li>
            <li v-if="offerPendingDelete.end_date">
              <i class="far fa-calendar-times me-1" aria-hidden="true" />
              Hasta {{ formatDate(offerPendingDelete.end_date) }}
            </li>
            <li>
              <i class="fas fa-box me-1" aria-hidden="true" />
              {{ offerPendingDelete.products?.length || 0 }} producto(s) vinculado(s)
            </li>
          </ul>
        </div>
        <p class="offer-delete-card__warn text-danger small mb-0">
          <i class="fas fa-ban me-1" />
          No se puede deshacer.
        </p>
        <div class="offer-delete-card__actions">
          <button
            type="button"
            class="btn offer-delete-btn-cancel"
            :disabled="deletingOfferId != null"
            @click="closeDeleteOfferModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn offer-delete-btn-confirm"
            :disabled="deletingOfferId != null"
            @click="confirmDeleteOffer"
          >
            <i v-if="deletingOfferId" class="fas fa-spinner fa-spin me-2" />
            <i v-else class="fas fa-trash me-2" />
            {{ deletingOfferId != null ? 'Eliminando…' : 'Eliminar oferta' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick, watch } from 'vue'
import { useToast } from 'vue-toastification'
import { getProducts } from '@/services/products'
import { getOffers, createOffer, updateOffer, deleteOffer as apiDeleteOffer } from '@/services/offers'
import { getCategoriesHierarchical } from '@/services/categories'
import { debounce } from '@/utils/text'

function pad2 (n) {
  return String(n).padStart(2, '0')
}
function formatDateInput (d) {
  if (!d || Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}
function isoToLocalDateTime (iso) {
  if (!iso) return { start: '', time: '09:00' }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { start: '', time: '09:00' }
  return {
    start: formatDateInput(d),
    time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
  }
}
function combineLocalDateTime (dateStr, timeStr) {
  if (!dateStr) return new Date(0)
  const t = (timeStr && timeStr.length >= 4) ? timeStr : '00:00'
  const parts = t.split(':')
  const hh = parseInt(parts[0], 10) || 0
  const mm = parseInt(parts[1], 10) || 0
  const dParts = String(dateStr).split('-')
  const y = parseInt(dParts[0], 10) || 0
  const mo = (parseInt(dParts[1], 10) || 1) - 1
  const day = parseInt(dParts[2], 10) || 1
  return new Date(y, mo, day, hh, mm, 0, 0)
}

function defaultScheduleRange () {
  const a = new Date()
  const b = new Date(a.getTime() + 7 * 24 * 60 * 60 * 1000)
  return {
    startDate: formatDateInput(a),
    startTime: '00:00',
    endDate: formatDateInput(b),
    endTime: '23:59'
  }
}

const toast = useToast()
const offers = ref([])
/** Hasta el primer `loadOffers` terminado, evita mostrar “no hay ofertas” en el arranque. */
const isLoadingOffers = ref(true)
const deletingOfferId = ref(null)
const showDeleteOfferModal = ref(false)
const offerPendingDelete = ref(null)
const products = ref([])
const categories = ref([])
const isSubmitting = ref(false)
const isEditing = ref(false)

const WIZARD_STEP_COUNT = 5
const wizardStep = ref(1)

const wizardStepMeta = [
  {
    id: 1,
    shortTitle: 'Datos',
    title: 'Nombre y descuento base',
    lead: 'Definí cómo se llama la promoción y qué porcentaje de descuento aplica por defecto. Es el valor base; en el siguiente paso podés acotarlo con mínimos, tope y alcance de público.',
    hint: 'Paso 1: identidad de la oferta'
  },
  {
    id: 2,
    shortTitle: 'Fechas',
    title: 'Inicio y fin de vigencia',
    lead: 'Indicá desde cuándo y hasta cuándo rige el descuento, con hora. Fuera de ese rango el sistema deja de aplicar la oferta aunque los productos sigan vinculados.',
    hint: 'Paso 2: calendario'
  },
  {
    id: 3,
    shortTitle: 'Reglas',
    title: 'Límites y público',
    lead: 'Definí si pedís un monto mínimo de compra por producto, un tope al descuento, y si la oferta es solo para mayoristas.',
    hint: 'Paso 3: condiciones de aplicación'
  },
  {
    id: 4,
    shortTitle: 'Productos',
    title: 'Qué se descuenta',
    lead: 'Elegí los productos que entran en la promoción. Podés buscarlos uno a uno o sumar un bloque entero según categoría.',
    hint: 'Paso 4: alcance de catálogo'
  },
  {
    id: 5,
    shortTitle: 'Revisar',
    title: 'Revisá y publicá',
    lead: 'Confirmá que todo coincida con lo acordado. Después de guardar, la oferta se aplicará según fechas y reglas en el sitio.',
    hint: 'Paso 5: confirmación'
  }
]

const currentStepMeta = computed(() => wizardStepMeta[wizardStep.value - 1] || wizardStepMeta[0])

const wizardProgress = computed(() => {
  if (WIZARD_STEP_COUNT <= 1) return '100%'
  return `${((wizardStep.value - 1) / (WIZARD_STEP_COUNT - 1)) * 100}%`
})

const canJumpToStep = (id) => id <= wizardStep.value

const goToStep = (id) => {
  if (!canJumpToStep(id)) return
  wizardStep.value = id
  scrollOfferFormTop()
}

const scrollOfferFormTop = () => {
  nextTick(() => {
    const el = document.querySelector('.offers-manager .offer-panel')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

const validateWizardStep = (step) => {
  if (step === 1) {
    if (!String(offerForm.value.name || '').trim()) {
      toast.error('Escribí un nombre para la oferta')
      return false
    }
    const d = Number(offerForm.value.discount)
    if (Number.isNaN(d) || d < 0 || d > 100) {
      toast.error('El descuento base debe estar entre 0 y 100')
      return false
    }
    return true
  }
  if (step === 2) {
    if (!offerForm.value.startDate || !offerForm.value.endDate) {
      toast.error('Completá las fechas de inicio y fin')
      return false
    }
    const start = combineLocalDateTime(offerForm.value.startDate, offerForm.value.startTime)
    const end = combineLocalDateTime(offerForm.value.endDate, offerForm.value.endTime)
    if (end.getTime() <= start.getTime()) {
      toast.error('El fin de vigencia debe ser posterior al inicio')
      return false
    }
    return true
  }
  if (step === 3) {
    const minL = numOrNull(offerForm.value.min_purchase)
    const maxD = numOrNull(offerForm.value.max_discount_amount)
    if (minL != null && maxD != null && minL > maxD) {
      toast.error('La compra mínima no puede ser mayor que el tope de descuento (revisá los dos montos)')
      return false
    }
    return true
  }
  if (step === 4) {
    if (!offerForm.value.products || offerForm.value.products.length === 0) {
      toast.error('Seleccioná al menos un producto para la oferta')
      return false
    }
    return true
  }
  return true
}

const goWizardNext = () => {
  if (!validateWizardStep(wizardStep.value)) return
  if (wizardStep.value < WIZARD_STEP_COUNT) {
    wizardStep.value += 1
    scrollOfferFormTop()
  }
}

const goWizardBack = () => {
  if (wizardStep.value > 1) {
    wizardStep.value -= 1
    scrollOfferFormTop()
  }
}

function numOrNull (v) {
  if (v === '' || v == null) return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

const hasOfferMin = (o) => o.min_purchase != null && o.min_purchase !== ''
const hasOfferCap = (o) => o.max_discount_amount != null && o.max_discount_amount !== ''

const _initialSched = defaultScheduleRange()
const offerForm = ref({
  name: '',
  discount: 0,
  startDate: _initialSched.startDate,
  startTime: _initialSched.startTime,
  endDate: _initialSched.endDate,
  endTime: _initialSched.endTime,
  min_purchase: '',
  max_discount_amount: '',
  distributors_only: false,
  products: []
})

const searchQuery = ref('')
const filteredProducts = ref([])
const isSearching = ref(false)
const selectedCategories = ref([])
const showCategorySelector = ref(false)
const isAddingCategoryProducts = ref(false)


// Computed property para aplanar las categorías jerárquicas
const flatCategories = computed(() => {
  const flattened = []
  
  categories.value.forEach(category => {
    // Agregar categoría principal
    flattened.push({
      ...category,
      displayName: category.name,
      level: 0
    })
    
    // Agregar subcategorías si existen
    if (category.children && category.children.length > 0) {
      category.children.forEach(child => {
        flattened.push({
          ...child,
          displayName: `  └ ${child.name}`, // Indentación visual
          level: 1,
          parentId: category.id
        })
      })
    }
  })
  
  return flattened
})

// Modificar la función onMounted para cargar todos los productos
onMounted(async () => {
  try {
    // Cargar todos los productos (sin paginación)
    const response = await getProducts({ page_size: 1000 }) // Un número grande para obtener todos
    products.value = response.results
      // Cargar categorías
    const categoriesResponse = await getCategoriesHierarchical()
    categories.value = categoriesResponse.results || categoriesResponse
    
    console.log('Productos cargados:', products.value.length)
    console.log('Categorías cargadas:', categories.value.length)
    if (products.value.length > 0) {
      console.log('Ejemplo de producto:', products.value[0])
    }
    if (categories.value.length > 0) {
      console.log('Ejemplo de categoría:', categories.value[0])
    }
    
    await loadOffers()
  } catch (error) {
    toast.error('Error al cargar los datos')
    isLoadingOffers.value = false
  }
})

const loadOffers = async () => {
  isLoadingOffers.value = true
  try {
    const response = await getOffers()
    offers.value = response.results || []
  } catch (error) {
    console.error('Error al cargar ofertas:', error)
    toast.error('Error al cargar las ofertas')
    offers.value = []
  } finally {
    isLoadingOffers.value = false
  }
}

const formatDate = (date) => {
  if (!date) return '—'
  return new Date(date).toLocaleString('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short'
  })
}

const getStatusBadgeClass = (offer) => {
  const now = new Date()
  const start = new Date(offer.start_date)
  const end = new Date(offer.end_date)
  
  if (now < start) return 'bg-warning'
  if (now > end) return 'bg-danger'
  return 'bg-success'
}

const getStatusText = (offer) => {
  const now = new Date()
  const start = new Date(offer.start_date)
  const end = new Date(offer.end_date)
  
  if (now < start) return 'Pendiente'
  if (now > end) return 'Finalizada'
  return 'Activa'
}

const handleSubmit = async () => {
  for (let s = 1; s <= 4; s++) {
    if (!validateWizardStep(s)) return
  }
  const start = combineLocalDateTime(offerForm.value.startDate, offerForm.value.startTime)
  const end = combineLocalDateTime(offerForm.value.endDate, offerForm.value.endTime)
  const minL = numOrNull(offerForm.value.min_purchase)
  const maxD = numOrNull(offerForm.value.max_discount_amount)
  try {
    isSubmitting.value = true
    const formData = {
      name: offerForm.value.name,
      discount: offerForm.value.discount,
      start_date: start.toISOString(),
      end_date: end.toISOString(),
      min_purchase: minL,
      max_discount_amount: maxD,
      distributors_only: !!offerForm.value.distributors_only,
      products: offerForm.value.products
    }
    if (isEditing.value) {
      await updateOffer(offerForm.value.id, formData)
      toast.success('Oferta actualizada exitosamente')
    } else {
      await createOffer(formData)
      toast.success('Oferta creada exitosamente')
    }
    resetForm()
    await loadOffers()
  } catch (error) {
    console.error('Error al guardar la oferta:', error)
    const msg = error.response?.data
    const str =
      (typeof msg === 'string' && msg) ||
      msg?.detail ||
      (Array.isArray(msg) && msg[0]) ||
      Object.values(msg || {})
        .flat()
        .filter(Boolean)
        .join(' ') ||
      'Error al guardar la oferta'
    toast.error(str)
  } finally {
    isSubmitting.value = false
  }
}

const editOffer = (offer) => {
  isEditing.value = true
  const s = isoToLocalDateTime(offer.start_date)
  const e = isoToLocalDateTime(offer.end_date)

  // offer.products may be an array of IDs or full objects depending on the API
  const rawProducts = offer.products || []
  const productIds = rawProducts.map(p => (typeof p === 'object' ? p.id : p))

  // If backend returned full objects, merge them into products.value for name/price lookup
  rawProducts.forEach(p => {
    if (typeof p === 'object' && p.id && !products.value.find(x => x.id === p.id)) {
      products.value.push(p)
    }
  })

  offerForm.value = {
    id: offer.id,
    name: offer.name,
    discount: Number(offer.discount),
    startDate: s.start,
    startTime: s.time,
    endDate: e.start,
    endTime: e.time,
    min_purchase: hasOfferMin(offer) ? String(offer.min_purchase) : '',
    max_discount_amount: hasOfferCap(offer) ? String(offer.max_discount_amount) : '',
    distributors_only: !!offer.distributors_only,
    products: productIds
  }
  wizardStep.value = 1
  scrollOfferFormTop()
}

const cancelEdit = () => {
  isEditing.value = false
  resetForm()
}

const openDeleteOfferModal = (offer) => {
  offerPendingDelete.value = offer
  showDeleteOfferModal.value = true
}

const closeDeleteOfferModal = () => {
  if (deletingOfferId.value != null) return
  showDeleteOfferModal.value = false
  offerPendingDelete.value = null
}

const handleDeleteModalKeydown = (e) => {
  if (e.key === 'Escape' && showDeleteOfferModal.value && deletingOfferId.value == null) {
    closeDeleteOfferModal()
  }
}

watch(showDeleteOfferModal, (open) => {
  if (typeof document === 'undefined') return
  if (open) {
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleDeleteModalKeydown)
  } else {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', handleDeleteModalKeydown)
  }
})

onUnmounted(() => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = ''
  document.removeEventListener('keydown', handleDeleteModalKeydown)
})

const confirmDeleteOffer = async () => {
  if (!offerPendingDelete.value) return
  const offerId = offerPendingDelete.value.id

  deletingOfferId.value = offerId
  try {
    await apiDeleteOffer(offerId)
    toast.success('Oferta eliminada')
    if (isEditing.value && offerForm.value.id === offerId) {
      resetForm()
    }
    showDeleteOfferModal.value = false
    offerPendingDelete.value = null
    await loadOffers()
  } catch (error) {
    console.error('Error al eliminar la oferta:', error)
    toast.error(error.response?.data?.detail || 'Error al eliminar la oferta')
  } finally {
    deletingOfferId.value = null
  }
}

const resetForm = () => {
  const d = defaultScheduleRange()
  offerForm.value = {
    name: '',
    discount: 0,
    startDate: d.startDate,
    startTime: d.startTime,
    endDate: d.endDate,
    endTime: d.endTime,
    min_purchase: '',
    max_discount_amount: '',
    distributors_only: false,
    products: []
  }
  selectedCategories.value = []
  showCategorySelector.value = false
  isAddingCategoryProducts.value = false
  isEditing.value = false
  wizardStep.value = 1
}

// Función de búsqueda que usa el backend (igual que el Navbar)
const doSearch = async () => {
  if (!searchQuery.value.trim()) {
    filteredProducts.value = []
    isSearching.value = false
    return
  }
  
  try {
    isSearching.value = true
    // Usar getProducts con search para traer más resultados (50 para admin)
    const response = await getProducts({ search: searchQuery.value, page_size: 50 })
    filteredProducts.value = response.results || []
  } catch (error) {
    console.error('Error en búsqueda:', error)
    filteredProducts.value = []
  } finally {
    isSearching.value = false
  }
}

// Debounce para no hacer muchas peticiones
const filterProducts = debounce(doSearch, 300)

const toggleProductSelection = (product) => {
  const index = offerForm.value.products.indexOf(product.id)
  if (index === -1) {
    offerForm.value.products.push(product.id)
  } else {
    offerForm.value.products.splice(index, 1)
  }
}

const removeProduct = (productId) => {
  const index = offerForm.value.products.indexOf(productId)
  if (index !== -1) {
    offerForm.value.products.splice(index, 1)
  }
}

const clearAllProducts = () => {
  if (confirm('¿Estás seguro de que quieres eliminar todos los productos seleccionados?')) {
    offerForm.value.products = []
    toast.success('Todos los productos han sido eliminados de la oferta')
  }
}

const getProductName = (productId) => {
  const product = products.value.find(p => p.id === productId)
  return product ? product.name : ''
}

const getProductBrand = (productId) => {
  const product = products.value.find(p => p.id === productId)
  return product ? product.brand : ''
}

const getProduct = (productId) => {
  return products.value.find(p => p.id === productId)
}

// Funciones para manejar categorías
const toggleCategorySelection = (category) => {
  const index = selectedCategories.value.indexOf(category.id)
  if (index === -1) {
    selectedCategories.value.push(category.id)
  } else {
    selectedCategories.value.splice(index, 1)
  }
}

const clearSelectedCategories = () => {
  selectedCategories.value = []
  toast.success('Selección de categorías limpiada')
}

const getCategoryProductCount = (categoryId) => {
  // Buscar en categorías principales
  const category = categories.value.find(c => c.id === categoryId)
  if (category && category.product_count !== undefined) {
    return category.product_count
  }
  
  // Buscar en subcategorías (children)
  for (const parentCategory of categories.value) {
    if (parentCategory.children) {
      const childCategory = parentCategory.children.find(child => child.id === categoryId)
      if (childCategory && childCategory.product_count !== undefined) {
        return childCategory.product_count
      }
    }
  }
  
  // Fallback: contar manualmente si no encontramos el product_count
  const count = products.value.filter(product => {
    return product.category === categoryId || 
           product.category_id === categoryId ||
           product.categoryId === categoryId
  }).length
  
  console.log(`Categoría ${categoryId}: ${count} productos (calculado manualmente)`)
  return count
}

const getCategoryName = (categoryId) => {
  // Buscar en categorías principales
  const category = categories.value.find(c => c.id === categoryId)
  if (category) {
    return category.name
  }
  
  // Buscar en subcategorías (children)
  for (const parentCategory of categories.value) {
    if (parentCategory.children) {
      const childCategory = parentCategory.children.find(child => child.id === categoryId)
      if (childCategory) {
        return childCategory.name
      }
    }
  }
  
  // Si no encontramos la categoría, mostrar el ID
  return `Categoría ${categoryId}`
}

const getTotalProductsFromSelectedCategories = () => {
  let totalCount = 0
  
  selectedCategories.value.forEach(categoryId => {
    const count = getCategoryProductCount(categoryId)
    totalCount += count
  })
  
  return totalCount
}

const addProductsFromCategories = async () => {
  if (selectedCategories.value.length === 0) return
  
  isAddingCategoryProducts.value = true
  
  try {
    const productIdsToAdd = new Set(offerForm.value.products)
    let totalNewProducts = 0
    
    // Para cada categoría seleccionada, obtener sus productos
    for (const categoryId of selectedCategories.value) {
      console.log(`Obteniendo productos para categoría ${categoryId}`)
      
      try {
        // Obtener el nombre de la categoría
        const categoryName = getCategoryName(categoryId)
        console.log(`Nombre de categoría: ${categoryName}`)
        
        // Obtener productos usando el nombre de la categoría
        const response = await getProducts({ 
          category_name: categoryName,
          page_size: 2000,
          is_available: true
        })
        
        let categoryProducts = response.results || []
        console.log(`API response para category_name=${categoryName}:`, {
          totalFound: categoryProducts.length,
          sampleProducts: categoryProducts.slice(0, 2).map(p => ({ 
            id: p.id, 
            name: p.name, 
            category: p.category, 
            category_name: p.category_name 
          }))
        })

        // Merge fetched product objects into products.value so name/price lookups work
        categoryProducts.forEach(product => {
          if (!products.value.find(p => p.id === product.id)) {
            products.value.push(product)
          }
        })
        
        // Si no encontramos productos con category_name, intentar obtener todos y filtrar localmente
        if (categoryProducts.length === 0) {
          console.log(`No se encontraron productos con category_name. Intentando filtro local...`)
          
          // Asegurar que tenemos productos cargados
          if (!products.value || products.value.length === 0) {
            console.log('Cargando todos los productos para filtro local...')
            const allProductsResponse = await getProducts({ page_size: 1000 })
            products.value = allProductsResponse.results || []
          }
          
          // Debug: ver estructura de productos
          console.log('Estructura de productos para debug:', {
            totalProducts: products.value.length,
            sampleProduct: products.value[0] ? {
              id: products.value[0].id,
              name: products.value[0].name,
              category: products.value[0].category,
              category_name: products.value[0].category_name,
              allKeys: Object.keys(products.value[0])
            } : null
          })
          
          // Filtrar productos localmente usando el ID de categoría
          categoryProducts = products.value.filter(product => {
            const match = product.category === categoryId || 
                         product.category_id === categoryId ||
                         product.categoryId === categoryId
            
            if (match) {
              console.log(`Match encontrado: Producto ${product.id} (${product.name}) - category: ${product.category}`)
            }
            
            return match
          })
          
          console.log(`Encontrados ${categoryProducts.length} productos filtrando localmente`)
        }
        
        // Agregar los IDs de productos a nuestro set
        categoryProducts.forEach(product => {
          if (!productIdsToAdd.has(product.id)) {
            productIdsToAdd.add(product.id)
            totalNewProducts++
            console.log(`Agregando producto ${product.id}: ${product.name}`)
          } else {
            console.log(`Producto ${product.id} ya estaba en la oferta`)
          }
        })
        
      } catch (error) {
        console.error(`Error al obtener productos de categoría ${categoryId}:`, error)
        
        // Fallback: filtrar productos localmente
        console.log(`Fallback: filtrando productos localmente para categoría ${categoryId}`)
        
        // Asegurar que tenemos productos cargados
        if (!products.value || products.value.length === 0) {
          console.log('Cargando todos los productos para fallback...')
          try {
            const allProductsResponse = await getProducts({ page_size: 1000 })
            products.value = allProductsResponse.results || []
          } catch (fallbackError) {
            console.error('Error cargando productos para fallback:', fallbackError)
            continue
          }
        }
        
        const categoryProducts = products.value.filter(product => {
          return product.category === categoryId || 
                 product.category_id === categoryId ||
                 product.categoryId === categoryId
        })
        
        console.log(`Encontrados ${categoryProducts.length} productos en fallback`)
        
        categoryProducts.forEach(product => {
          if (!productIdsToAdd.has(product.id)) {
            productIdsToAdd.add(product.id)
            totalNewProducts++
          }
        })
      }
    }
    
    offerForm.value.products = Array.from(productIdsToAdd)
    const newCount = offerForm.value.products.length
    
    // Limpiar selección de categorías
    selectedCategories.value = []
    
    // Mostrar mensaje de éxito
    if (totalNewProducts > 0) {
      toast.success(`Se agregaron ${totalNewProducts} productos nuevos. Total: ${newCount} productos únicos`)
    } else {
      toast.info('Todos los productos de las categorías seleccionadas ya estaban incluidos en la oferta')
    }
    
    // Cambiar a la vista de productos para ver los productos agregados
    showCategorySelector.value = false
    
  } catch (error) {
    console.error('Error al agregar productos de categorías:', error)
    toast.error('Error al agregar productos de las categorías seleccionadas')
  } finally {
    isAddingCategoryProducts.value = false
  }
}
</script>

<style scoped>
/* ===== Container ===== */
.offers-manager {
  padding: 1rem;
}

/* ===== Panels ===== */
.offer-panel {
  background: rgba(0,0,0,0.15);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  overflow: hidden;
}
.offer-panel--editing {
  border-color: rgba(212, 175, 55, 0.25);
  box-shadow: 0 0 0 1px rgba(212, 175, 55, 0.08);
}
.edit-mode-alert {
  font-size: 0.82rem;
  line-height: 1.45;
  color: #c9b88a;
  background: rgba(212, 175, 55, 0.08);
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 8px;
  padding: 0.55rem 0.75rem;
}
.edit-mode-alert strong { color: #e8d89a; }
.offers-loading-state .offers-loading-spin { width: 2rem; height: 2rem; }

.offer-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.offer-panel-header .btn-cancel-edit { flex-shrink: 0; }

.panel-title {
  color: #ccc;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

.offer-panel-body {
  padding: 1.25rem;
}

/* ===== Forms ===== */
.form-label, label {
  color: #888;
  font-size: 0.82rem;
  font-weight: 600;
}

h5 { color: #ccc; }

.form-control, .form-select {
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
  color: #ccc;
  font-size: 0.84rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.form-control::placeholder { color: #444; }
.form-control:focus, .form-select:focus {
  background: rgba(0,0,0,0.35);
  border-color: rgba(var(--primary-color-rgb), 0.4);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.15);
  color: #ddd;
}

.input-group-text {
  background: rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
  font-size: 0.84rem;
}

/* ===== Tab Buttons ===== */
.tab-btn {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
  font-size: 0.82rem;
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.tab-btn:hover { color: #ccc; border-color: rgba(255,255,255,0.15); }
.tab-btn-active {
  background: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  color: #fff !important;
}

/* ===== Buttons ===== */
.btn-cancel-edit {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
  font-size: 0.82rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.btn-cancel-edit:hover { color: #ccc; border-color: rgba(255,255,255,0.15); }

.btn-submit {
  background: var(--primary-color);
  border: none;
  color: #fff;
  font-size: 0.84rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.btn-submit:hover:not(:disabled) { background: var(--primary-dark); color: #fff; }
.btn-submit:disabled { opacity: 0.5; color: #fff; }

.btn-add-from-categories {
  background: var(--primary-color);
  border: none;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.btn-add-from-categories:hover:not(:disabled) { background: var(--primary-dark); color: #fff; }
.btn-add-from-categories:disabled { opacity: 0.5; color: #fff; }

.btn-clear-selection {
  background: rgba(251,191,36,0.1);
  border: 1px solid rgba(251,191,36,0.25);
  color: #fbbf24;
  font-size: 0.78rem;
  border-radius: 6px;
  transition: all 0.15s ease;
}
.btn-clear-selection:hover { background: rgba(251,191,36,0.18); color: #fbbf24; }

.btn-remove-item {
  background: rgba(248,113,113,0.1);
  border: 1px solid rgba(248,113,113,0.2);
  color: #f87171;
  border-radius: 6px;
  transition: all 0.15s ease;
}
.btn-remove-item:hover { background: rgba(248,113,113,0.2); color: #f87171; }

/* ===== Table ===== */
.offers-table {
  color: #aaa;
  border-collapse: separate;
  border-spacing: 0;
}

.offers-table thead th, .table thead th {
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  color: #666;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.65rem 0.75rem;
}

.offers-table td, .table td {
  border-color: rgba(255,255,255,0.04);
  vertical-align: middle;
  font-size: 0.84rem;
  padding: 0.75rem;
}

/* ===== Action Buttons ===== */
.btn-action {
  font-size: 0.78rem;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.btn-action-edit {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
}
.btn-action-edit:hover { color: #ccc; border-color: rgba(255,255,255,0.15); }

.btn-action-delete {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(248,113,113,0.2);
  color: #f87171;
}
.btn-action-delete:hover { background: rgba(248,113,113,0.08); border-color: rgba(248,113,113,0.3); }

.btn-group .btn { margin: 0 0.15rem; }

/* ===== Badges ===== */
.badge { padding: 0.35em 0.6em; font-size: 0.72rem; border-radius: 6px; }
.badge.bg-success { background: rgba(var(--success-color-rgb), 0.2) !important; color: var(--success-color); }
.badge.bg-warning { background: rgba(251,191,36,0.15) !important; color: #fbbf24; }
.badge.bg-danger { background: rgba(248,113,113,0.15) !important; color: #f87171; }

/* ===== Lists ===== */
.products-list, .categories-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
}

.list-group-item {
  background: rgba(0,0,0,0.2);
  border-color: rgba(255,255,255,0.04);
  color: #aaa;
  font-size: 0.84rem;
  transition: background 0.15s ease;
}
.list-group-item:hover { background: rgba(0,0,0,0.3); }
.list-group-item.active {
  background: rgba(var(--primary-color-rgb), 0.2);
  border-color: rgba(var(--primary-color-rgb), 0.3);
  color: var(--primary-color);
}

.list-group-item.subcategory {
  padding-left: 2rem;
  background: rgba(255,255,255,0.015);
  border-left: 2px solid var(--primary-color);
}
.list-group-item.subcategory:hover { background: rgba(255,255,255,0.03); }

.list-group-item small { opacity: 0.7; }

.price { font-weight: 600; color: var(--primary-color); }

/* ===== Selected Products ===== */
.selected-products {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
}

.categories-selector {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
  background: rgba(0,0,0,0.15);
}

/* Resumen al elegir categorías (sin clases .alert) */
.category-selection-summary {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.75rem 0.9rem;
  margin-bottom: 0.75rem;
  background: rgba(var(--primary-color-rgb), 0.1);
  border: 1px solid rgba(var(--primary-color-rgb), 0.22);
  border-radius: 10px;
  color: #b8c4bc;
  font-size: 0.84rem;
  line-height: 1.45;
}
.category-selection-summary__icon {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(var(--primary-color-rgb), 0.25);
  color: var(--primary-light);
  font-size: 0.9rem;
}
.category-selection-summary strong { color: #c8e0d4; }

/* Modal eliminar oferta */
.offer-delete-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.65);
  animation: offer-delete-fade-in 0.2s ease;
}
@keyframes offer-delete-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.offer-delete-card {
  width: 100%;
  max-width: 420px;
  background: #161616;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.55);
  padding: 0;
  animation: offer-delete-pop 0.22s cubic-bezier(0.32, 0.72, 0, 1);
}
@keyframes offer-delete-pop {
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.offer-delete-card__head {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 1.1rem 1.15rem 0.25rem;
  position: relative;
}
.offer-delete-card__icon {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(248, 113, 113, 0.12);
  color: #f87171;
  font-size: 1.1rem;
  border: 1px solid rgba(248, 113, 113, 0.2);
}
.offer-delete-card__title {
  flex: 1;
  font-size: 1.05rem;
  font-weight: 600;
  color: #e8e8e8;
  margin: 0.15rem 0 0;
  line-height: 1.3;
  padding-right: 2.25rem;
}
.offer-delete-card__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #666;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
}
.offer-delete-card__close:hover:not(:disabled) {
  color: #ccc;
  background: rgba(255, 255, 255, 0.06);
}
.offer-delete-card__close:disabled { opacity: 0.4; cursor: not-allowed; }
.offer-delete-card__lead,
.offer-delete-card__warn { padding: 0 1.15rem; }
.offer-delete-card__warn {
  padding-top: 0.6rem;
  font-weight: 500;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 0.75rem;
  padding-bottom: 0.5rem;
}
.offer-delete-card__summary {
  margin: 0.4rem 1.15rem 0;
  padding: 0.85rem 0.9rem;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
}
.offer-delete-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #e0e0e0;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  word-break: break-word;
}
.offer-delete-meta li {
  margin-bottom: 0.2rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.2rem 0.35rem;
}
.offer-delete-meta li:last-child { margin-bottom: 0; }
.offer-delete-card__actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.15rem 1.1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(0, 0, 0, 0.15);
  border-radius: 0 0 14px 14px;
}
.offer-delete-btn-cancel {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #aaa;
  font-size: 0.86rem;
  border-radius: 8px;
  padding: 0.45rem 0.9rem;
}
.offer-delete-btn-cancel:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #ddd;
}
.offer-delete-btn-confirm {
  background: rgba(248, 113, 113, 0.15);
  border: 1px solid rgba(248, 113, 113, 0.35);
  color: #f87171;
  font-size: 0.86rem;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.45rem 0.9rem;
}
.offer-delete-btn-confirm:hover:not(:disabled) {
  background: rgba(248, 113, 113, 0.25);
  border-color: rgba(248, 113, 113, 0.45);
  color: #fca5a5;
}
.offer-delete-btn-confirm:disabled,
.offer-delete-btn-cancel:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
@media (max-width: 400px) {
  .offer-delete-card__actions {
    flex-direction: column-reverse;
  }
  .offer-delete-btn-cancel,
  .offer-delete-btn-confirm {
    width: 100%;
  }
}

/* ===== Spinners ===== */
.spinner-border.text-primary { color: var(--primary-color) !important; }

/* ===== Fecha + hora separadas ===== */
.sub-label {
  font-size: 0.72rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.25rem;
}
.datetime-row { margin-top: 0.25rem; }
.datetime-row .form-control { min-height: 38px; }

.vigencia-side-by-side { margin-top: 0.25rem; }
.vigencia-card {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  padding: 0.85rem 1rem;
}
.vigencia-card-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
}
.offer-cond-cell { max-width: 9rem; line-height: 1.35; }

/* Paso 3: reglas de monto y público */
.offer-rules-step { margin-top: 0.25rem; }
.rules-step-section-title {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--primary-color);
  margin: 0 0 0.75rem;
}
.rules-step-section-title--spaced { margin-top: 1.5rem; }
.rule-field-card {
  background: rgba(0,0,0,0.22);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 1rem 1.1rem 1.05rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}
.rule-field-card__head {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.rule-field-card__badge {
  flex-shrink: 0;
  width: 2.4rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(var(--primary-color-rgb), 0.2);
  border: 1px solid rgba(var(--primary-color-rgb), 0.35);
  color: var(--primary-light);
  font-size: 1rem;
}
.rule-field-card__badge--cap {
  background: rgba(96,165,250,0.1);
  border-color: rgba(96,165,250,0.25);
  color: #93c5fd;
}
.rule-field-card__label {
  margin: 0;
  color: #e8e8e8;
  font-size: 0.95rem;
  font-weight: 600;
}
.rule-field-card__sub {
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  color: #777;
  line-height: 1.35;
}
.input-group-offer .input-group-text {
  background: rgba(0,0,0,0.35);
  border: 1px solid rgba(255,255,255,0.1);
  color: var(--primary-light);
  font-weight: 600;
  font-size: 0.9rem;
}
.input-group-offer .form-control {
  background: rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.1);
  color: #e0e0e0;
  min-height: 42px;
}
.input-group-offer .form-control:focus {
  border-color: rgba(var(--primary-color-rgb), 0.45);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.12);
  color: #fff;
}
.rule-field-card__explain {
  margin: 0;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.05);
  font-size: 0.8rem;
  line-height: 1.45;
  color: #9a9a9a;
}
.rule-field-card__explain strong { color: #b8b8b8; }
.rule-audience-card {
  background: rgba(0,0,0,0.18);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 1rem 1.1rem;
}
.rule-audience-card .form-check-input {
  width: 2.4rem;
  height: 1.15rem;
  margin-top: 0.2rem;
  cursor: pointer;
}
.rule-audience-card .form-check-label {
  color: #ddd;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  margin-left: 0.35rem;
}
.rule-audience-hint {
  margin: 0.6rem 0 0 0;
  padding-left: 0.1rem;
  font-size: 0.8rem;
  line-height: 1.45;
  color: #888;
  max-width: 40rem;
}
.rule-audience-hint strong { color: #a8a8a8; }
@media (max-width: 575px) {
  .rule-field-card__head { flex-wrap: wrap; }
}

/* ===== Asistente paso a paso ===== */
.header-sub { max-width: 36rem; line-height: 1.4; }
.wizard-stepper { position: relative; padding: 0 0.25rem; }
.wizard-stepper-track {
  height: 4px;
  background: rgba(255,255,255,0.08);
  border-radius: 4px;
  margin-bottom: 0.5rem;
  overflow: hidden;
}
.wizard-stepper-line {
  height: 100%;
  width: var(--progress, 0%);
  background: linear-gradient(90deg, var(--primary-dark), var(--primary-color));
  border-radius: 4px;
  transition: width 0.25s ease;
}
.wizard-stepper-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.35rem;
}
.wizard-stepper-item { flex: 1; min-width: 0; text-align: center; }
.wizard-stepper-btn {
  width: 100%;
  border: none;
  background: transparent;
  color: #888;
  font-size: 0.7rem;
  padding: 0.25rem;
  line-height: 1.2;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  transition: color 0.15s;
}
.wizard-stepper-btn:hover:not(:disabled) { color: #aaa; }
.wizard-stepper-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.wizard-stepper-item.is-active .wizard-stepper-btn { color: var(--primary-light); }
.wizard-stepper-item.is-active .wizard-stepper-num { border-color: var(--primary-color); background: rgba(var(--primary-color-rgb), 0.25); }
.wizard-stepper-item.is-done .wizard-stepper-num { border-color: var(--primary-color); color: var(--primary-color); }
.wizard-stepper-num {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.12);
  font-size: 0.75rem;
  font-weight: 700;
}
.wizard-stepper-label { display: block; max-width: 5.5rem; }
.wizard-step-hint { font-size: 0.8rem; }
.wizard-step { animation: stepFade 0.2s ease; }
@keyframes stepFade { from { opacity: 0.3; } to { opacity: 1; } }
.step-intro { margin-bottom: 1.15rem; padding-bottom: 0.75rem; border-bottom: 1px solid rgba(255,255,255,0.06); }
.step-title { color: #ddd; font-size: 1rem; font-weight: 600; margin: 0 0 0.4rem; }
.step-lead { color: #888; font-size: 0.86rem; line-height: 1.5; margin: 0; max-width: 48rem; }
.wizard-nav {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255,255,255,0.08);
}
.btn-wizard-back {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #aaa;
  font-size: 0.88rem;
  border-radius: 8px;
  padding: 0.45rem 0.9rem;
}
.btn-wizard-back:hover { color: #ddd; border-color: rgba(255,255,255,0.15); }
.btn-wizard-next {
  background: var(--primary-color);
  border: none;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.45rem 1.1rem;
}
.btn-wizard-next:hover { background: var(--primary-dark); color: #fff; }
.review-card {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 1rem 1.15rem;
}
.review-dl { margin: 0; }
.review-row {
  display: grid;
  grid-template-columns: minmax(8rem, 12rem) 1fr;
  gap: 0.5rem 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  font-size: 0.86rem;
}
.review-row:last-child { border-bottom: none; }
.review-dl dt { color: #666; font-weight: 600; margin: 0; }
.review-dl dd { color: #ccc; margin: 0; }
@media (max-width: 576px) {
  .review-row { grid-template-columns: 1fr; }
  .wizard-stepper-label { display: none !important; }
}
</style>
